/**
 * src/lib/templateModel.js — mô hình dữ liệu thư viện mẫu, dạng hàm thuần.
 *
 * Không import Firebase. Dùng chung ở ba nơi và phải cho kết quả giống nhau:
 *  - trình duyệt (trang /mau, trang quản trị /admin/mau)
 *  - scripts/fetch-templates.mjs lúc build (kéo mẫu đã xuất bản về để prerender)
 *  - src/data/templates.js (gộp mẫu sinh từ code và mẫu tải lên)
 *
 * HAI LOẠI MẪU
 *  - 'sinh': sinh ngay trên trình duyệt từ code (shared/excel). Sửa công thức
 *    một lần là mọi bản tải về đều đúng. Khai báo trong src/data/templates.js.
 *  - 'tep':  tệp dựng sẵn (Excel, Word, PDF) tải lên từ /admin/mau. Dùng cho
 *    mẫu quá phức tạp để sinh bằng code, hoặc dựng bằng công cụ khác.
 *
 * Chống phân hoá phiên bản cho loại 'tep': mỗi lần thay tệp là một đường dẫn
 * Storage mới (mau/<slug>/v<n>/…), bản ghi chỉ trỏ tới bản hiện hành và giữ
 * lịch sử kèm ghi chú thay đổi. Không bao giờ ghi đè lên tệp người dùng đã tải.
 *
 * PHÂN LOẠI THEO NHIỀU CHIỀU (không phải cây thư mục)
 *  - nhom:     loại tài liệu (chiều dọc)
 *  - nganh:    ngành áp dụng (chiều ngang), 'chung' = mọi ngành
 *  - giaiDoan: vòng gọi vốn phù hợp
 *  - doSau:    lite / standard / pro (dùng để lọc, không hiện cho người dùng)
 *  - nhanNgan: nhãn mô tả ngắn trên thẻ, ví dụ "Đầy đủ 3 báo cáo, 36 tháng"
 */
import { chuanHoaSlug, PLAYBOOK_STAGES } from './playbookModel.js';

export { chuanHoaSlug };

export const NHOM_MAU = [
  { slug: 'dashboard', label: 'Dashboard', moTa: 'Nhập số ngay trên trang, xem dashboard, in hoặc lưu PDF gửi nhà đầu tư.' },
  { slug: 'mo-hinh-tai-chinh', label: 'Mô hình tài chính', moTa: 'Dự phóng doanh thu, chi phí, dòng tiền và nhu cầu vốn.' },
  { slug: 'bao-cao-tai-chinh', label: 'Báo cáo tài chính & báo cáo nhà đầu tư', moTa: 'Ba báo cáo chuẩn startup, báo cáo quản trị tháng, investor update.' },
  { slug: 'dinh-gia', label: 'Định giá', moTa: 'VC Method, Scorecard, Berkus, so sánh, DCF.' },
  { slug: 'co-phan', label: 'Cổ phần & cấu trúc vốn', moTa: 'Cap table, chia cổ phần sáng lập, SAFE, ESOP, pha loãng.' },
  { slug: 'chi-so-hoat-dong', label: 'Chỉ số hoạt động (KPI)', moTa: 'Bảng theo dõi KPI theo ngành.' },
  { slug: 'due-diligence', label: 'Due diligence & pháp lý', moTa: 'Checklist thẩm định, mục lục data room, use of funds.' },
  // Việc quản lý bản thân vòng gọi vốn: theo ai, tới bước nào, báo cáo gì sau khi nhận vốn.
  { slug: 'quan-ly-goi-von', label: 'Quản lý vòng gọi vốn', moTa: 'Theo dõi nhà đầu tư, báo cáo cho quỹ, kế hoạch sử dụng vốn.' },
];

export const NGANH_MAU = [
  { slug: 'chung', label: 'Mọi ngành' },
  { slug: 'saas', label: 'SaaS' },
  { slug: 'marketplace', label: 'Marketplace / nền tảng' },
  { slug: 'ecommerce', label: 'Thương mại điện tử / D2C' },
  { slug: 'fnb', label: 'F&B / bán lẻ chuỗi' },
  { slug: 'fintech', label: 'Fintech' },
  { slug: 'edtech', label: 'Edtech' },
  { slug: 'healthtech', label: 'Healthtech' },
  { slug: 'logistics', label: 'Logistics' },
  { slug: 'agritech', label: 'Agritech' },
  { slug: 'deeptech', label: 'Deeptech / phần cứng' },
];

export const DO_SAU = {
  lite: { label: 'Lite', moTa: 'Gọn, cho vòng angel / pre-seed' },
  standard: { label: 'Standard', moTa: 'Đầy đủ hơn, cho vòng seed' },
  pro: { label: 'Pro', moTa: 'Chi tiết, cho Series A' },
};

export const GIAI_DOAN_MAU = PLAYBOOK_STAGES;

/**
 * Nhãn ngắn trên thẻ mẫu. "Lite" không giúp founder chọn giữa mẫu 12 tháng và
 * mẫu 36 tháng, nên ưu tiên mô tả cụ thể:
 *   1. nhanNgan do người quản trị nhập;
 *   2. nếu không có: suy từ nội dung mẫu (đủ 3 báo cáo? bao nhiêu tháng?);
 *   3. cuối cùng mới dùng mô tả độ sâu.
 */
export function nhanMoTaMau(mau = {}) {
  if (mau.nhanNgan) return mau.nhanNgan;
  const tenTab = (mau.tabs || []).map((t) => String(t.ten || '').toUpperCase());
  const du3BaoCao = ['KQKD', 'LCTT', 'CDKT'].every((x) => tenTab.includes(x));
  const thang = String(mau.title || '').match(/(\d{1,2})\s*tháng/i)?.[1];
  if (du3BaoCao) return thang ? `Đầy đủ 3 báo cáo, ${thang} tháng` : 'Đầy đủ 3 báo cáo tài chính';
  if (thang) return `${thang} tháng`;
  return DO_SAU[mau.doSau]?.moTa || '';
}

/** Định dạng tệp được phép tải lên. Không nhận .xlsm: mẫu công khai không được chứa macro. */
export const DINH_DANG = {
  xlsx: { label: 'Excel', contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  docx: { label: 'Word', contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
  pdf: { label: 'PDF', contentType: 'application/pdf' },
};

export const TRAN_KICH_THUOC = 20 * 1024 * 1024;

/** Slug hệ thống dùng trong /mau/… — không cho mẫu dùng. */
export const RESERVED_TEMPLATE_SLUGS = ['_ban-moi', 'nhom', 'nganh'];

export const nhomBySlug = (slug) => NHOM_MAU.find((n) => n.slug === slug) || null;
export const nganhBySlug = (slug) => NGANH_MAU.find((n) => n.slug === slug) || null;

export function dinhDangCuaTen(ten) {
  const m = /\.([a-z0-9]+)$/i.exec(String(ten || ''));
  const ext = m ? m[1].toLowerCase() : '';
  return DINH_DANG[ext] ? ext : '';
}

export function dinhDangKichThuoc(byte) {
  const n = Number(byte) || 0;
  if (n <= 0) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

/** Tên tệp khi người dùng tải về: không dấu, không khoảng trắng, giữ đuôi. */
export function tenTepTaiVe(slug, phienBan, ext, ngonNgu = 'vi') {
  const goc = chuanHoaSlug(slug) || 'mau';
  return `${goc}${ngonNgu === 'en' ? '-en' : ''}-v${Math.max(1, Number(phienBan) || 1)}.${ext}`;
}

/** Mẫu có bản tiếng Anh không: mẫu sinh từ code luôn có; mẫu tệp khi đã tải bản EN. */
export const coBanTiengAnh = (mau) => Boolean(mau && (mau.kind === 'sinh' || mau.fileEnUrl));

/** Gắn tệp tiếng Anh vừa tải lên. Không giữ lịch sử: mỗi phiên bản vẫn là một đường dẫn riêng. */
export function ganTepTiengAnh(mau, tep) {
  return { ...mau, fileEnUrl: tep.url, fileEnPath: tep.path, fileEnName: tep.name, fileEnSize: tep.size, fileEnFormat: tep.format, fileEnPhienBan: tep.phienBan };
}

/** Bỏ bản tiếng Anh khỏi mẫu (tệp trên Storage vẫn giữ). */
export function boTepTiengAnh(mau) {
  return { ...mau, fileEnUrl: '', fileEnPath: '', fileEnName: '', fileEnSize: 0, fileEnFormat: '', fileEnPhienBan: 0 };
}

function plain(value, max = 4000) { return String(value ?? '').trim().slice(0, max); }
function httpsUrl(value) { const url = plain(value, 2000); return /^https:\/\//.test(url) ? url : ''; }
function danhSach(value, max = 20, doDai = 300) {
  return (Array.isArray(value) ? value : []).map((x) => plain(x, doDai)).filter(Boolean).slice(0, max);
}
function trongTap(values, cho) {
  const hopLe = new Set(cho);
  return [...new Set((Array.isArray(values) ? values : []).filter((x) => hopLe.has(x)))];
}
function isoDate(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  if (typeof value?.toDate === 'function') return value.toDate().toISOString().slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return '';
}

/** Chuẩn hoá một mẫu loại 'tep' đọc từ Firestore hoặc từ form quản trị. */
export function chuanHoaMau(raw = {}) {
  const fileFormat = DINH_DANG[raw.fileFormat] ? raw.fileFormat : dinhDangCuaTen(raw.fileName);
  return {
    slug: chuanHoaSlug(raw.slug || raw.title),
    kind: 'tep',
    title: plain(raw.title, 180),
    excerpt: plain(raw.excerpt, 400),
    maMau: plain(raw.maMau, 40).toUpperCase(),
    // Nhóm 'dashboard' chỉ dành cho dashboard viết bằng code, mẫu tải lên không vào được.
    nhom: nhomBySlug(raw.nhom) && raw.nhom !== 'dashboard' ? raw.nhom : 'mo-hinh-tai-chinh',
    nganh: trongTap(raw.nganh, NGANH_MAU.map((n) => n.slug)),
    giaiDoan: trongTap(raw.giaiDoan, GIAI_DOAN_MAU),
    doSau: DO_SAU[raw.doSau] ? raw.doSau : 'lite',
    nhanNgan: plain(raw.nhanNgan, 40),
    phienBan: Math.max(0, Math.floor(Number(raw.phienBan) || 0)),
    ghiChuPhienBan: plain(raw.ghiChuPhienBan, 300),
    fileUrl: httpsUrl(raw.fileUrl),
    filePath: plain(raw.filePath, 300),
    fileName: plain(raw.fileName, 120),
    fileSize: Math.max(0, Math.floor(Number(raw.fileSize) || 0)),
    fileFormat,
    // Bản tiếng Anh (tuỳ chọn): tệp riêng, đánh số phiên bản riêng, ở mau/<slug>/en-v<n>/.
    fileEnUrl: httpsUrl(raw.fileEnUrl),
    fileEnPath: plain(raw.fileEnPath, 300),
    fileEnName: plain(raw.fileEnName, 120),
    fileEnSize: Math.max(0, Math.floor(Number(raw.fileEnSize) || 0)),
    fileEnFormat: DINH_DANG[raw.fileEnFormat] ? raw.fileEnFormat : dinhDangCuaTen(raw.fileEnName),
    fileEnPhienBan: Math.max(0, Math.floor(Number(raw.fileEnPhienBan) || 0)),
    lichSu: (Array.isArray(raw.lichSu) ? raw.lichSu : []).slice(-30).map((h) => ({
      phienBan: Math.max(1, Math.floor(Number(h?.phienBan) || 1)),
      filePath: plain(h?.filePath, 300),
      fileSize: Math.max(0, Math.floor(Number(h?.fileSize) || 0)),
      ghiChu: plain(h?.ghiChu, 300),
      ngay: plain(h?.ngay, 10),
    })),
    bienSo: danhSach(raw.bienSo, 12),
    tabs: (Array.isArray(raw.tabs) ? raw.tabs : [])
      .map((t) => ({ ten: plain(t?.ten, 60), moTa: plain(t?.moTa, 300) }))
      .filter((t) => t.ten).slice(0, 20),
    luuY: danhSach(raw.luuY, 10, 400),
    baiLienQuan: chuanHoaSlug(raw.baiLienQuan),
    congCu: (Array.isArray(raw.congCu) ? raw.congCu : [])
      .map((c) => ({ slug: chuanHoaSlug(c?.slug), label: plain(c?.label, 80) }))
      .filter((c) => c.slug && c.label).slice(0, 4),
    status: raw.status === 'PUBLISHED' ? 'PUBLISHED' : 'DRAFT',
    publishedAt: isoDate(raw.publishedAt),
    updatedAt: isoDate(raw.updatedAt),
  };
}

/**
 * Kiểm tra trước khi lưu. Trả về danh sách lỗi (rỗng là hợp lệ).
 * slugsKhac: slug của mẫu sinh từ code — không được trùng.
 */
export function kiemTraMau(mau, status, slugsKhac = []) {
  const loi = [];
  if (!mau.title) loi.push('Cần tiêu đề.');
  if (!mau.slug) loi.push('Cần đường dẫn.');
  if (RESERVED_TEMPLATE_SLUGS.includes(mau.slug)) loi.push(`Đường dẫn "${mau.slug}" dành riêng cho hệ thống.`);
  if (slugsKhac.includes(mau.slug)) loi.push(`Đường dẫn "${mau.slug}" đã dùng cho một mẫu sinh từ code.`);
  if (status === 'PUBLISHED') {
    if (!mau.excerpt) loi.push('Cần mô tả ngắn trước khi xuất bản.');
    if (!mau.fileUrl || !mau.fileFormat) loi.push('Cần tải tệp mẫu lên trước khi xuất bản.');
    if (!mau.nganh.length) loi.push('Chọn ít nhất một ngành (hoặc "Mọi ngành").');
  }
  return loi;
}

/**
 * Ghi nhận một tệp mới vừa tải lên: tăng phiên bản, đẩy tệp cũ vào lịch sử.
 * Tệp cũ không bị xoá — ai đã tải bản cũ vẫn đối chiếu được.
 */
export function ganTepMoi(mau, tep, ghiChu, ngay = new Date().toISOString().slice(0, 10)) {
  const lichSu = mau.fileUrl
    ? [...mau.lichSu, { phienBan: mau.phienBan || 1, filePath: mau.filePath, fileSize: mau.fileSize, ghiChu: mau.ghiChuPhienBan, ngay: mau.updatedAt || ngay }]
    : mau.lichSu;
  return {
    ...mau,
    phienBan: tep.phienBan,
    fileUrl: tep.url, filePath: tep.path, fileName: tep.name, fileSize: tep.size, fileFormat: tep.format,
    ghiChuPhienBan: plain(ghiChu, 300),
    lichSu: lichSu.slice(-30),
  };
}

/** Đọc ô nhập dạng "Tên | mô tả" mỗi dòng. */
export function tachDong(text, khoa = ['ten', 'moTa']) {
  return String(text || '').split('\n').map((line) => {
    const [a, ...rest] = line.split('|');
    return { [khoa[0]]: (a || '').trim(), [khoa[1]]: rest.join('|').trim() };
  }).filter((x) => x[khoa[0]]);
}

/** Lọc danh sách mẫu theo nhóm và ngành ('' = tất cả). Mẫu 'chung' khớp mọi ngành. */
export function locMau(list, { nhom = '', nganh = '' } = {}) {
  return list.filter((t) => (!nhom || t.nhom === nhom)
    && (!nganh || (t.nganh || []).includes(nganh) || (t.nganh || []).includes('chung')));
}
